import React from "react";
import {
  Dialog,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Box,
  ListItemIcon,
  ListItemButton,
  CircularProgress,
} from "@mui/material";
import { Public, Store } from "@mui/icons-material";

import { useTranslation } from "../../../../hooks/useTranslation";
import { useMarketplaceStore } from "../../../../store/useMarketplaceStore";
import { getDomainByCountryCode } from "../../../product/components/utils";

type AmazonViewItem = {
  id: number | string;
  title: string;
  asin: string;
  imageUrl?: string;
  quantityOrdered: number;
  itemPrice: number;
};

type AmazonViewDialogProps = {
  open: boolean;
  onClose: () => void;
  orderItems?: AmazonViewItem[];
};

export const AmazonViewDialog: React.FC<AmazonViewDialogProps> = ({
  open,
  onClose,
  orderItems,
}) => {
  const { translate } = useTranslation(
    "pages.orders.list.fields.actions.amazonView",
  );
  const selectedCountry = useMarketplaceStore((state) => state.selectedCountry);

  if (!selectedCountry?.countryCode) {
    return <CircularProgress />;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogContent>
        <List>
          {orderItems?.map((item) => (
            <ListItem key={item.id} sx={{ gap: 2 }}>
              <ListItemAvatar>
                <Avatar
                  variant="square"
                  src={item.imageUrl}
                  sx={{ width: 96, height: 96 }}
                />
              </ListItemAvatar>

              <Box flex={1}>
                <ListItemText
                  primary={item.title}
                  secondary={
                    <>
                      <div>ASIN: {item.asin}</div>
                      <div>
                        {translate("orderedQuantity")}: {item.quantityOrdered} —
                        ${item.itemPrice.toFixed(2)}
                      </div>
                    </>
                  }
                />
              </Box>

              <Box display="flex" flexDirection="column" gap={1}>
                <ListItemButton
                  onClick={() =>
                    window.open(
                      `https://amazon.com/dp/${item.asin.toUpperCase()}`,
                      "_blank",
                    )
                  }
                >
                  <ListItemIcon>
                    <Public />
                  </ListItemIcon>
                  <ListItemText primary={translate("amazonViewUs")} />
                </ListItemButton>

                <ListItemButton
                  onClick={() =>
                    window.open(
                      `https://www.amazon.${getDomainByCountryCode(
                        selectedCountry.countryCode,
                      )}/dp/${item.asin.toUpperCase()}`,
                      "_blank",
                    )
                  }
                >
                  <ListItemIcon>
                    <Store />
                  </ListItemIcon>
                  <ListItemText
                    primary={translate("amazonViewLocal", {
                      localStore: selectedCountry.countryName,
                    })}
                  />
                </ListItemButton>
              </Box>
            </ListItem>
          ))}
        </List>
      </DialogContent>
    </Dialog>
  );
};
